import type { ProjectState } from "@livcg/schema";
import { EyeOff, PowerOff } from "lucide-react";
import { useImperativeHandle } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, cn, focusRing, StatusDot, tileButton, tileOnAir } from "../../ui/index.js";
import { OVERLAY_GROUPS, overlayModeLabel } from "../overlayModes.js";
import { rosterModeHasData, noDataTitle } from "./overlayData.js";
import { useOverlayExtras } from "./useOverlayExtras.js";
import { useOverlaySwitch } from "./useOverlaySwitch.js";

/** Imperative handle so the parent (keyboard shortcut: Esc) can hide whatever
 *  is currently on air without reaching into this component's state. */
export type OverlayBoardHandle = { hideCurrent: () => void };

type Props = {
  slug: string;
  state: ProjectState;
  controlRef?: React.Ref<OverlayBoardHandle>;
  /** Kill every active surface at once — current overlay + VAR. Owned by the
   *  parent since VAR lives outside this component (Shift+Esc / ALL OFF). */
  onAllOff: () => void;
  /** Klassik tab: also shows the classicOnly (manual fallback) groups. */
  showClassicOnly?: boolean;
};

export function OverlayBoard(props: Props) {
  const extrasCtx = useOverlayExtras(props.state);
  const sw = useOverlaySwitch(props.slug, props.state, extrasCtx.extras);
  return <OverlayBoardView {...props} extrasCtx={extrasCtx} sw={sw} />;
}

/** The board over a caller-owned overlay switch — Classic and Deck share one
 *  instance with their hotkeys, so Esc and the fullscreen return stay in sync. */
export function OverlayBoardView({
  slug,
  state,
  controlRef,
  onAllOff,
  showClassicOnly,
  extrasCtx,
  sw,
}: Props & {
  extrasCtx: ReturnType<typeof useOverlayExtras>;
  sw: ReturnType<typeof useOverlaySwitch>;
}) {
  const { extras, customText, setCustomText: onCustomText, standingsInfo } = extrasCtx;
  const { current, gridActive, isActive, busyMode, toggle, apply, hideCurrent } = sw;
  const anyActive = Boolean(current) || Boolean(gridActive) || Boolean(state.varOverlay?.visible);

  useImperativeHandle(controlRef, () => ({ hideCurrent }));

  return (
    <div className="flex h-full flex-col rounded-lg border border-border bg-surface-muted/40">
      {/* Fixed height: so the header doesn't grow when the on-air chip appears —
          otherwise the whole tiles block jumps down ~9px. */}
      <header className="flex h-11 shrink-0 items-center justify-between gap-2 border-b border-divider px-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
          Overlay boshqaruv
        </h3>
        {current ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-live/40 bg-live-soft px-2 py-0.5 text-[11px] font-semibold text-live">
              <StatusDot tone="live" pulse />
              {overlayModeLabel(current)}
            </span>
            <Button
              variant="secondary"
              size="xs"
              onClick={hideCurrent}
              leadingIcon={<EyeOff className="h-3 w-3" />}
            >
              Yashirish <span className="opacity-50">(Esc)</span>
            </Button>
          </div>
        ) : (
          <span className="text-[11px] text-ink-faint">Efirda overlay yo'q</span>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-5">
          {OVERLAY_GROUPS.filter((g) => showClassicOnly || !g.classicOnly).map((g) => (
            <section
              key={g.name}
              className="overflow-hidden rounded-lg border border-border bg-surface"
            >
              <div className="flex items-center justify-between gap-2 border-b border-divider px-3 py-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
                    {g.name}
                  </h4>
                  <span className="rounded bg-surface-elevated px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-ink-faint">
                    {g.modes.length}
                  </span>
                </div>
                {g.name === "Sponsor" && (
                  <label
                    className="flex cursor-pointer items-center gap-1.5 text-[11px] text-ink-muted hover:text-ink"
                    title="Sponsor strip little panellari ostida birga chiqadi HAMDA 1-/2-taymda har 5 daqiqada sponsor titri avto efirga beriladi (SKORE)"
                  >
                    <input
                      type="checkbox"
                      checked={state.autoSponsor ?? false}
                      onChange={(e) =>
                        void sendRaw("sponsorAuto.set", { slug, enabled: e.target.checked })
                      }
                      className="h-3.5 w-3.5 accent-[color:var(--p-accent)]"
                    />
                    Auto sponsor
                  </label>
                )}
              </div>
              <div
                className="grid gap-1.5 p-3"
                style={{
                  gridTemplateColumns: g.cols
                    ? `repeat(${g.cols}, minmax(0, 1fr))`
                    : "repeat(auto-fill, minmax(132px, 1fr))",
                }}
              >
                {g.modes.map((mode) => {
                  const active = isActive(mode.id);
                  const noData = !active && !rosterModeHasData(mode.id, state, extras);
                  const disabled = (busyMode !== null && busyMode !== mode.id) || noData;
                  const busy = busyMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      data-anchor={`tile:${mode.id}`}
                      type="button"
                      onClick={() => toggle(mode.id)}
                      disabled={disabled}
                      title={noData ? noDataTitle(mode.id) : undefined}
                      className={cn(
                        tileButton,
                        "group flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px]",
                        "disabled:cursor-not-allowed disabled:opacity-45 disabled:border-dashed",
                        active && tileOnAir,
                        busy && "opacity-60",
                      )}
                    >
                      <span className="min-w-0 truncate pl-1 font-medium">{mode.label}</span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        {mode.side && (
                          <span className="rounded bg-surface-elevated px-1 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
                            {mode.side === "home" ? "H" : "A"}
                          </span>
                        )}
                        {active && (
                          <span className="inline-flex items-center gap-1 rounded bg-live px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 motion-reduce:hidden" />
                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                            </span>
                            EFIRDA
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              {g.modes.some((m) => m.id === "custom") && (
                <div className="flex flex-col gap-1.5 border-t border-divider px-3 py-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-faint">
                    Custom matn (BIG universal sarlavha)
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={customText}
                      onChange={(e) => onCustomText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && customText.trim()) apply("custom");
                      }}
                      placeholder="Sarlavha matni…"
                      className="min-w-0 flex-1 rounded border border-border bg-surface px-2.5 py-1.5 text-[12px] text-ink-soft placeholder:text-ink-faint focus:outline-none focus:border-ink-faint"
                    />
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => apply("custom")}
                      disabled={!customText.trim() || (busyMode !== null && busyMode !== "custom")}
                    >
                      {current === "custom" ? "Yangilash" : "Efirga"}
                    </Button>
                  </div>
                </div>
              )}
              {g.modes.some((m) => m.id === "tournament_1_8") && (
                <div className="flex items-center justify-between gap-2 border-t border-divider px-3 py-2">
                  <span className="text-[11px] text-ink-faint">
                    {standingsInfo.count
                      ? `${standingsInfo.count} jamoa`
                      : "Jadval sinxronlanmagan"}
                  </span>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={standingsInfo.sync}
                    disabled={!standingsInfo.canSync || standingsInfo.syncing}
                  >
                    {standingsInfo.syncing ? "Sinxronlanmoqda…" : "Jadvalni sinxronlash"}
                  </Button>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>

      <footer className="border-t border-divider p-3">
        <button
          type="button"
          onClick={onAllOff}
          disabled={!anyActive}
          title="Barcha overlay va VAR'ni yashirish (Shift+Esc)"
          className={cn(
            focusRing,
            "flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-[12.5px] font-bold uppercase tracking-[0.08em] transition-colors",
            anyActive
              ? "border-live bg-[repeating-linear-gradient(45deg,var(--p-live-soft)_0,var(--p-live-soft)_10px,transparent_10px,transparent_20px)] text-live hover:border-live hover:bg-live hover:bg-none hover:text-white"
              : "cursor-not-allowed border-border bg-surface text-ink-faint",
          )}
        >
          <PowerOff className="h-4 w-4" strokeWidth={2.5} />
          ALL OFF
          <kbd className="rounded border border-current/40 px-1 py-0.5 text-[10px] font-semibold leading-none opacity-80">
            ⇧Esc
          </kbd>
        </button>
      </footer>
    </div>
  );
}
